import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Table, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { createNonStdEntry, getNonStdSummary } from '../../services/nonstandardOps';
import { NonStdSummary } from '../../types/nonstandardOps';
import ChargeLinesPanel from './ChargeLinesPanel';
import CustomerReplyForm from './CustomerReplyForm';
import DestroyRecordsPanel from './DestroyRecordsPanel';
import EntryEditor from './EntryEditor';
import EntryTimeline from './EntryTimeline';
import LabelsPanel from './LabelsPanel';
import SubmitRoundModal from './SubmitRoundModal';

interface Props {
  instructionId: number;
  /** execute：仓库执行（只记录，不接触收费）；review：终审（管理员管收费）；view：只读（客户可回复） */
  mode: 'execute' | 'review' | 'view';
  /** 汇总数据变化时通知外层（用于刷新外层的提示数字） */
  onSummary?: (summary: NonStdSummary) => void;
  /** 修改了已经生成过收费记录的收费行时通知外层 */
  onRequiresRecalc?: () => void;
  /** 不显示收费明细（执行页里的只读视图：执行阶段不接触收费） */
  hideCharges?: boolean;
  /** 不显示容器标签卡片（"确认完成"阶段自己有容器核对） */
  hideLabels?: boolean;
  /** 不显示销毁记录卡片（"确认完成"阶段自己有） */
  hideDestroy?: boolean;
}

/** 鼠标放在问号上显示：这个按钮做什么、和每条记录的"对客户可见"开关的区别 */
const ROUND_HINT = '表示本轮告一段落、请客户反馈：在时间线加一条“本轮结束”的记录，指令标记为“等待客户反馈”，客户页面会提示回复，轮次加 1。客户能看到哪些记录，由每条记录自己的“对客户可见”开关决定，本轮至少要有一条可见记录才能提交。不会结束执行阶段，客户回复后可以继续记录。';

/** 库存非标准操作指令的工作台：执行记录时间线（分轮次）+ 客户回复 + 收费行（只有管理员和客户看得到）。执行页、终审页、详情页共用。 */
const NonStdWorkbench: React.FC<Props> = ({ instructionId, mode, onSummary, onRequiresRecalc, hideCharges, hideLabels, hideDestroy }) => {
  const [summary, setSummary] = useState<NonStdSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roundOpen, setRoundOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getNonStdSummary(instructionId);
      setSummary(data);
      setError('');
      if (onSummary) onSummary(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || '加载失败');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;
  if (error && !summary) return <Alert type="error" showIcon message={error} />;
  if (!summary) return null;

  const { viewer } = summary;
  const isOperator = viewer.is_operator;
  const isAdmin = viewer.is_admin;
  // 执行记录阶段提交后记录和标签已固化为快照，任何人都不能再改（收费行不受影响）
  const frozen = !!summary.records_frozen;
  const editable = mode !== 'view' && isOperator && summary.can_edit && !frozen;
  // 收费：执行页完全不出现（员工不接触收费）；终审页管理员管理；详情页管理员/客户只读
  const showCharges = mode !== 'execute' && !hideCharges && viewer.can_view_charges;
  const chargeEditable = mode === 'review' && isAdmin && summary.can_edit;
  const currentRoundEntries = summary.entries.filter(e => e.round_no === summary.current_round && e.kind === 'note');
  const lastRoundSubmitted = summary.current_round > 1;

  return (
    <div>
      {mode === 'review' && isOperator && frozen ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="执行记录已在执行阶段提交并固化为快照，这里的记录只读。在这里编辑的是提交给客户的最终数据：收费明细、指令执行结果附件和终审备注。"
        />
      ) : null}
      {isOperator && summary.waiting_customer ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`第 ${summary.current_round - 1} 轮结果已提交给客户，正在等待客户反馈。客户回复后在下面继续记录；如果不需要等了，也可以直接继续处理。`}
        />
      ) : null}
      <Card
        size="small"
        title={<Space><span>执行记录</span>{summary.current_round > 1 ? <span style={{ fontWeight: 400, color: '#8c8c8c' }}>当前第 {summary.current_round} 轮</span> : null}</Space>}
        extra={editable ? (
          <Space size={4}>
            <Button type="primary" ghost disabled={currentRoundEntries.length === 0} onClick={() => setRoundOpen(true)}>提交本轮结果，请客户反馈</Button>
            <Tooltip title={ROUND_HINT}>
              <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
          </Space>
        ) : null}
        style={{ marginBottom: 12 }}
      >
        <EntryTimeline entries={summary.entries} editable={editable} showInternal={isOperator} isAdmin={isAdmin} onChanged={load} />
        {editable ? (
          <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>添加记录{lastRoundSubmitted ? `（第 ${summary.current_round} 轮）` : ''}</div>
            <EntryEditor
              draftKey={`nonstd_entry_draft_${instructionId}`}
              onSubmit={async payload => {
                await createNonStdEntry(instructionId, payload);
                message.success('已添加');
                await load();
              }}
            />
          </div>
        ) : null}
        {summary.can_reply ? <CustomerReplyForm instructionId={instructionId} waiting={summary.waiting_customer} onReplied={load} /> : null}
      </Card>
      {isOperator && summary.labels && !hideLabels ? (
        <Card size="small" title={`容器标签${summary.labels.length > 0 ? `（${summary.labels.length}）` : ''}`} style={{ marginBottom: 12 }}>
          <LabelsPanel
            instructionId={instructionId}
            instructionCode={summary.instruction.system_code}
            labels={summary.labels}
            nextNumber={summary.label_next_number}
            groups={summary.label_groups}
            lastGroup={summary.label_last_group}
            canEdit={mode === 'execute' && editable}
            canPrint={!!summary.can_print_labels}
            canConsume={mode === 'execute' && !!summary.confirm?.can_consume}
            showPlacement={mode !== 'execute'}
            onChanged={load}
          />
        </Card>
      ) : null}
      {/* 销毁记录：执行页可以记录；其他页面只读，有记录才显示。客户的汇总里没有这份数据 */}
      {isOperator && summary.destroy && !hideDestroy && (mode === 'execute' || summary.destroy.records.length > 0) ? (
        <Card
          size="small"
          className="nonstd-destroy-card"
          title={`销毁记录${summary.destroy.records.length > 0 ? `（${summary.destroy.records.length}）` : ''}`}
          style={{ marginBottom: 12 }}
        >
          <DestroyRecordsPanel
            instructionId={instructionId}
            info={mode === 'execute' ? summary.destroy : { ...summary.destroy, can_edit: false }}
            onChanged={load}
          />
        </Card>
      ) : null}
      {mode === 'review' && isOperator && (summary.rounds || []).length > 0 ? (
        <Card size="small" title="按轮次汇总" style={{ marginBottom: 12 }}>
          <Table
            size="small"
            rowKey="round"
            pagination={false}
            dataSource={summary.rounds}
            columns={[
              { title: '轮次', dataIndex: 'round', width: 80, render: (v: number) => `第 ${v} 轮` },
              { title: '员工记录数', dataIndex: 'entry_count', width: 100 },
              { title: '其中对客户可见', dataIndex: 'visible_count', width: 120 },
              { title: '客户回复数', dataIndex: 'reply_count', width: 100 },
              { title: '已提交给客户', dataIndex: 'submitted', width: 120, render: (v: boolean) => (v ? '是' : '否') },
            ]}
          />
        </Card>
      ) : null}
      {showCharges ? (
        <Card size="small" title="收费明细">
          <ChargeLinesPanel
            instructionId={instructionId}
            lines={summary.charge_lines}
            totals={summary.confirmed_totals}
            isAdmin={isAdmin}
            canEdit={chargeEditable}
            onChanged={opts => {
              load();
              if (opts?.requiresRecalc && onRequiresRecalc) onRequiresRecalc();
            }}
          />
        </Card>
      ) : null}
      <SubmitRoundModal
        open={roundOpen}
        instructionId={instructionId}
        round={summary.current_round}
        entries={currentRoundEntries}
        onClose={() => setRoundOpen(false)}
        onSubmitted={() => { setRoundOpen(false); load(); }}
      />
    </div>
  );
};

export default NonStdWorkbench;
